import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { AxiosInterceptorService } from "src/app/header-interceptor";
import { UserService } from "../core/user.service";

export interface InlineEditSaveEvent {
  tableName: string;
  fieldName: string;
  salesOrderId: number;
  recordId?: number;
  newValue: any;
}

@Component({
  standalone: false,
  selector: "app-inline-edit-cell",
  templateUrl: "./inline-edit-cell.component.html",
  styleUrls: ["./inline-edit-cell.component.scss"],
})
export class InlineEditCellComponent implements OnChanges {
  private readonly genericInlineEditEndpoint =
    "GenericInlineEditor/UpdateGenericInlineUpdate";
  private readonly legacySalesOrderEndpoint = "SalesOrder/UpdateSalesOrderField";

  @Input() value: any;
  @Input() salesOrderId: number;
  @Input() recordId: number;
  @Input() tableName = "SalesOrder";
  @Input() fieldName = "";
  @Input() fieldType = "";
  @Input() placeholder = "";
  @Input() emptyText = "-";
  @Input() canEdit = true;
  @Input() trimValue = true;
  @Input() showNotifications = true;
  @Input() apiEndpoint = "";
  @Input() displayAsTextbox = false;
  @Input() displayAsDxTextbox = false;
  @Input() showEditOnHover = false;

  @Output() saved = new EventEmitter<InlineEditSaveEvent>();
  @Output() canceled = new EventEmitter<void>();

  isEditing = false;
  isSaving = false;
  draftValue: any = "";
  displayValue = "";

  constructor(
    private api: AxiosInterceptorService,
    private userService: UserService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.displayValue = this.formatValue(this.value);
    }
  }

  startEdit(): void {
    if (!this.canEdit || this.isSaving) {
      return;
    }
    this.isEditing = true;
    this.draftValue = this.value ?? "";
  }

  cancelEdit(): void {
    if (this.isSaving) {
      return;
    }
    this.isEditing = false;
    this.draftValue = this.value ?? "";
    this.canceled.emit();
  }

  async save(): Promise<void> {
    if (this.isSaving || !this.isEditing) {
      return;
    }

    const normalizedValue = this.normalizeValue(this.draftValue);
    if (this.isSameValue(normalizedValue, this.value)) {
      this.isEditing = false;
      return;
    }

    const resolvedEndpoint = this.resolveApiEndpoint();
    const resolvedRecordId = this.toRecordId(this.recordId ?? this.salesOrderId);

    if (!resolvedEndpoint || !this.tableName || !this.fieldName || !resolvedRecordId) {
      this.notifyError("Inline edit configuration is incomplete.");
      return;
    }

    this.isSaving = true;
    const payload = this.buildPayload(normalizedValue, resolvedRecordId);

    try {
      const response = await this.api.post(resolvedEndpoint, payload);
      if (response?.data?.completed) {
        this.value = normalizedValue;
        this.displayValue = this.formatValue(normalizedValue);
        this.isEditing = false;
        this.saved.emit({
          tableName: this.tableName,
          fieldName: this.fieldName,
          salesOrderId: this.salesOrderId,
          recordId: resolvedRecordId,
          newValue: normalizedValue,
        });
        if (this.showNotifications) {
          this.userService.showSnackBar(response?.data?.message || "Updated successfully.");
        }
      } else {
        this.notifyError(response?.data?.message || "Unable to update value.");
      }
    } catch (error) {
      this.notifyError(error?.message || "Unable to update value.");
    } finally {
      this.isSaving = false;
    }
  }

  onKeyDown(event: any): void {
    const key = event?.event?.key ?? event?.key;
    if (key === "Escape") {
      this.cancelEdit();
    }
  }

  private normalizeValue(value: any): any {
    if (typeof value === "string") {
      return this.trimValue ? value.trim() : value;
    }
    return value;
  }

  private isSameValue(nextValue: any, currentValue: any): boolean {
    if (nextValue === currentValue) {
      return true;
    }
    const isNextEmpty = nextValue === null || nextValue === undefined || nextValue === "";
    const isCurrentEmpty = currentValue === null || currentValue === undefined || currentValue === "";
    return isNextEmpty && isCurrentEmpty;
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined || value === "") {
      return this.emptyText;
    }
    return String(value);
  }

  private resolveApiEndpoint(): string {
    if (
      !this.apiEndpoint ||
      this.apiEndpoint === this.legacySalesOrderEndpoint ||
      this.apiEndpoint === `/${this.legacySalesOrderEndpoint}` ||
      this.apiEndpoint === this.genericInlineEditEndpoint ||
      this.apiEndpoint === `/${this.genericInlineEditEndpoint}`
    ) {
      return this.genericInlineEditEndpoint;
    }

    return this.apiEndpoint;
  }

  private buildPayload(value: any, recordId: number): any {
    if (this.resolveApiEndpoint() === this.genericInlineEditEndpoint) {
      return {
        tableName: this.tableName,
        fieldName: this.fieldName,
        fieldValue: this.toGenericFieldValue(value),
        fieldType: this.fieldType || this.inferFieldType(value),
        recordId,
      };
    }

    return {
      TableName: this.tableName,
      FieldName: this.fieldName,
      SalesOrderId: recordId,
      NewValue: value,
    };
  }

  private inferFieldType(value: any): string {
    if (value instanceof Date) {
      return "date";
    }
    switch (typeof value) {
      case "number":
        return "number";
      case "boolean":
        return "boolean";
      default:
        return "string";
    }
  }

  private toGenericFieldValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value);
  }

  private toRecordId(value: any): number {
    const parsedValue = Number.parseInt(String(value), 10);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  private notifyError(message: string): void {
    if (this.showNotifications) {
      this.userService.showSnackBar(message);
    }
  }
}
